// react
import { useMemo } from 'react';
// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import { Dialog, DialogContent, DialogTitle } from '@mui/material';

// api
import type {
  GetEnergyTreeNodeDto,
  PostEnergyTreeNodeDto,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';
import {
  useGetEnergyGroupQuery,
  usePostEnergyGroupEnergyTreeNodeMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { WritableOnly } from '@/components/access-control';

// feature-utils
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import AddNodeForm from '../../forms/AddNodeForm';
import { toPostEnergyTreeNodeDto } from '../../forms/addNodeForm.mappers';
import type { AddNodeFormValues } from '../../forms/addNodeForm.schema';

interface Unit {
  UnitId: number;
  Name: string;
  Tags?: Array<{ TagId: number; Text: string }>;
  UnitSensors?: Array<{ Sensor?: { SensorId: number } }>;
  usedInNode?: string;
  notSupportedReason?: string;
}

type AddNodeDialogProps = {
  open: boolean;
  onClose: () => void;
  energyTreeNode?: GetEnergyTreeNodeDto;
  units: Unit[];
  mode?: 'server' | 'local';
  onConfirm?: (node: PostEnergyTreeNodeDto) => void;
};

const AddNodeDialog = ({
  open,
  onClose,
  energyTreeNode,
  units,
  mode = 'server',
  onConfirm,
}: AddNodeDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['energyxplorer-tree-tab', 'validation']);

  const { energyGroupId } = useParams({ strict: false }) as {
    energyGroupId?: string;
  };

  const parsedEnergyGroupId = energyGroupId
    ? parseInt(energyGroupId, 10)
    : undefined;

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: parsedEnergyGroupId! },
    {
      skip: !parsedEnergyGroupId,
    },
  );

  const [postEnergyTreeNode, { isLoading: isCreating }] =
    usePostEnergyGroupEnergyTreeNodeMutation();

  const inclusions = useMemo(() => {
    if (!selectedEnergyGroup) return [];
    const nodesFound = getInitiativeInclusionsForNode(
      energyTreeNode?.energyTreeNodeId ?? '',
      selectedEnergyGroup,
    );
    return nodesFound;
  }, [energyTreeNode?.energyTreeNodeId, selectedEnergyGroup]);

  const virtualInitiativeInclusions = useMemo(
    () =>
      (inclusions || []).filter(({ viaNode }) => {
        if (!viaNode || typeof viaNode !== 'object') return false;
        const node = viaNode as { dataStreams?: unknown[] };
        return !node.dataStreams || node.dataStreams.length === 0;
      }),
    [inclusions],
  );

  const handleSubmit = async (values: AddNodeFormValues) => {
    if (!values.type) {
      dispatch(
        showSnackbar({
          message: t('add_node_dialog.select_node_type', {
            ns: 'energyxplorer-tree-tab',
          }),
          severity: 'warning',
        }),
      );
      return;
    }

    const parentId = energyTreeNode?.energyTreeNodeId ?? null;

    // ✅ LOCAL MODE: do not call API, return created node to parent
    if (mode === 'local') {
      const dto = toPostEnergyTreeNodeDto(values, parentId);
      const createdNode = {
        ...dto,
        parentId: parentId ?? null,
      };

      if (typeof onConfirm !== 'function') {
        // Defensive: avoid silent failure
        dispatch(
          showSnackbar({
            message: t('shared_dialog.local_mode_handler_missing', {
              ns: 'energyxplorer-tree-tab',
            }),
            severity: 'error',
          }),
        );
        return;
      }

      onConfirm(createdNode);
      dispatch(
        showSnackbar({
          message: t('add_node_dialog.node_added_success', {
            ns: 'energyxplorer-tree-tab',
          }),
          severity: 'success',
        }),
      );
      onClose();
      return;
    }

    // ✅ SERVER MODE (existing behavior): POST immediately
    if (!parsedEnergyGroupId) return;

    const dto = toPostEnergyTreeNodeDto(values, parentId);
    const payload = {
      ...dto,
      energyGroupId: parsedEnergyGroupId,
    };

    const result = await postEnergyTreeNode({
      groupId: parsedEnergyGroupId,
      postEnergyTreeNodeDto: payload,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('add_node_dialog.node_added_success', {
            ns: 'energyxplorer-tree-tab',
          }),
          severity: 'success',
        }),
      );
      onClose();
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Dialog
      data-testid="orgchart-add-node-dialog"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle data-testid="add-node-dialog-title">
        {t('add_node_dialog.title', {
          ns: 'energyxplorer-tree-tab',
          item: energyTreeNode?.name,
        })}
      </DialogTitle>
      <WritableOnly>
        <DialogContent>
          <AddNodeForm
            units={units}
            virtualInitiativeInclusions={virtualInitiativeInclusions}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isSubmitting={isCreating}
          />
        </DialogContent>
      </WritableOnly>
    </Dialog>
  );
};

export default AddNodeDialog;
